import React, { useLayoutEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SectionWrapper from '../UI/SectionWrapper';

gsap.registerPlugin(ScrollTrigger);

const audiences = [
  {
    title: "Home Owners",
    desc: "Seeking peace, improved wellbeing, and a harmonious sanctuary for their families.",
    icon: "home",
    color: "text-rose-500",
    glow: "bg-rose-400",
    palette: { primary: "#F43F5E", secondary: "#FDA4AF", accent: "#FBBF24" }
  },
  {
    title: "Landowners",
    desc: "Dedicated to the stewardship of their land, seeking energetic balance and vitality.",
    icon: "land",
    color: "text-emerald-600",
    glow: "bg-emerald-400",
    palette: { primary: "#059669", secondary: "#6EE7B7", accent: "#A3E635" }
  },
  {
    title: "Property Developers",
    desc: "Optimizing land energy to ensure successful projects and positive living outcomes.",
    icon: "developer",
    color: "text-blue-600",
    glow: "bg-blue-400",
    palette: { primary: "#2563EB", secondary: "#93C5FD", accent: "#F59E0B" }
  },
  {
    title: "Architects",
    desc: "Knowing / Clearing what's on site to foster successful project delivery and outcomes.",
    icon: "architect",
    color: "text-violet-600",
    glow: "bg-violet-400",
    palette: { primary: "#7C3AED", secondary: "#C4B5FD", accent: "#EC4899" }
  },
  {
    title: "Estate Agents",
    desc: "Resolving energetic 'stuckness' to facilitate smooth property transitions and sales.",
    icon: "agent",
    color: "text-cyan-600",
    glow: "bg-cyan-400",
    palette: { primary: "#0891B2", secondary: "#67E8F9", accent: "#FBBF24" }
  },
  {
    title: "Businesses",
    desc: "Clearing workplace energies to foster productivity, abundance, and employee wellbeing.",
    icon: "business",
    color: "text-slate-600",
    glow: "bg-amber-400",
    palette: { primary: "#475569", secondary: "#94A3B8", accent: "#10B981" }
  }
];

const AudienceIllustration = ({ type, color, glow, palette }) => {
  const iconRef = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      // Existing animations preserved
      if (type === 'home') {
        gsap.to(".home-heart", { scale: 1.2, opacity: 1, duration: 2, repeat: -1, yoyo: true, ease: "sine.inOut" });
      }
      if (type === 'land') {
        gsap.to(".land-leaf", { rotate: 10, duration: 3, repeat: -1, yoyo: true, ease: "sine.inOut" });
        gsap.from(".land-roots path", { drawSVG: "0%", strokeDashoffset: 100, duration: 2, stagger: 0.2, repeat: -1 });
      }
      // Added simple animations for others to ensure liveliness
      if (type === 'developer') gsap.to(".dev-block", { y: -5, duration: 2, stagger: 0.2, repeat: -1, yoyo: true });
      if (type === 'agent') gsap.to(".agent-key", { y: -5, rotate: 5, duration: 2, repeat: -1, yoyo: true });
      if (type === 'business') gsap.to(".biz-arrow", { y: -3, opacity: 0.5, duration: 1.5, repeat: -1, yoyo: true });

    }, iconRef);
    return () => ctx.revert();
  }, [type]);

  return (
    <div ref={iconRef} className="w-24 h-24 mb-6 flex items-center justify-center relative">
      <div className={`absolute inset-0 ${glow} rounded-full blur-2xl opacity-10 group-hover:opacity-25 transition-opacity duration-500`} />
      <svg width="80" height="80" viewBox="0 0 80 80" className="relative z-10 overflow-visible">
        {/* Animated Background Aura */}
        <circle
          cx="40"
          cy="40"
          r="38"
          fill="none"
          stroke={`url(#grad-${type})`}
          strokeWidth="0.5"
          className="animate-[spin_30s_linear_infinite]"
          style={{ transformOrigin: '40px 40px' }}
        />

        <defs>
          <linearGradient id={`grad-${type}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={palette.primary} stopOpacity="0.1" />
            <stop offset="50%" stopColor={palette.secondary} stopOpacity="0.3" />
            <stop offset="100%" stopColor={palette.accent} stopOpacity="0.1" />
          </linearGradient>
        </defs>

        {type === 'home' && (
          <g>
            {/* House Body */}
            <path
              d="M20 45 L40 25 L60 45 V65 H20 V45Z"
              fill="none"
              stroke={palette.primary}
              strokeWidth="1.5"
              strokeLinejoin="round"
            />
            {/* Glowing Heart/Sanctuary - Gold Accent */}
            <circle cx="40" cy="50" r="6" fill={palette.accent} fillOpacity="0.2" stroke={palette.accent} strokeWidth="1" className="home-heart" />
            <path d="M35 65 V55 H45 V65" fill="none" stroke={palette.primary} strokeWidth="1.5" />
            {/* Ethereal Roof Sparkles - Secondary Color */}
            <circle cx="40" cy="18" r="1.5" fill={palette.secondary} className="animate-pulse" />
            <circle cx="30" cy="25" r="1" fill={palette.secondary} className="animate-pulse [animation-delay:0.5s]" />
            <circle cx="50" cy="25" r="1" fill={palette.secondary} className="animate-pulse [animation-delay:1s]" />
          </g>
        )}

        {type === 'land' && (
          <g>
            {/* Mound */}
            <path d="M15 65 Q40 55 65 65" fill="none" stroke={palette.primary} strokeWidth="1.5" />
            {/* Growing Sprout - Accent Lime */}
            <path d="M40 60 L40 35" stroke={palette.primary} strokeWidth="2" strokeLinecap="round" />
            <path
              d="M40 35 C40 35 55 25 55 40 C55 48 40 45 40 45"
              fill={palette.accent}
              fillOpacity="0.2"
              stroke={palette.accent}
              strokeWidth="1.5"
              className="land-leaf"
              style={{ transformOrigin: '40px 35px' }}
            />
            <path
              d="M40 45 C40 45 25 35 25 50 C25 58 40 55 40 55"
              fill={palette.secondary}
              fillOpacity="0.2"
              stroke={palette.secondary}
              strokeWidth="1.5"
              className="land-leaf-2 animate-[bounce_4s_infinite]"
              style={{ transformOrigin: '40px 45px' }}
            />
            {/* Energy Roots */}
            <g className="land-roots opacity-40">
              <path d="M40 60 Q30 70 25 75" stroke={palette.primary} strokeWidth="0.5" strokeDasharray="2 2" />
              <path d="M40 60 Q50 70 55 75" stroke={palette.primary} strokeWidth="0.5" strokeDasharray="2 2" />
            </g>
          </g>
        )}

        {type === 'developer' && (
          <g>
            {/* Foundation Grid */}
            <rect x="20" y="55" width="40" height="15" fill="none" stroke={palette.secondary} strokeWidth="1" strokeDasharray="2 2" opacity="0.5" />
            {/* Rising Blocks - Primary Blue */}
            <rect x="25" y="40" width="10" height="25" fill="none" stroke={palette.primary} strokeWidth="1.5" className="dev-block" />
            <rect x="38" y="25" width="10" height="40" fill="none" stroke={palette.primary} strokeWidth="1.5" className="dev-block [animation-delay:0.2s]" />
            <rect x="51" y="45" width="10" height="20" fill="none" stroke={palette.primary} strokeWidth="1.5" className="dev-block [animation-delay:0.4s]" />
            {/* Crane/Action Accent */}
            <line x1="15" y1="55" x2="65" y2="55" stroke={palette.secondary} strokeWidth="0.5" opacity="0.3" />
            <circle cx="56" cy="20" r="2" fill={palette.accent} className="animate-ping" />
          </g>
        )}

        {type === 'architect' && (
          <g>
            {/* Golden Ratio Spiral / Curve - Primary Violet */}
            <path
              d="M60 60 C60 20 20 20 20 50 C20 70 40 70 40 60"
              fill="none"
              stroke={palette.primary}
              strokeWidth="1.5"
              strokeDasharray="4 4"
              className="animate-dash"
            />
            {/* Drafting Compass - Accent Pink */}
            <g className="animate-swing" style={{ transformOrigin: '40px 30px' }}>
              <path d="M35 60 L40 30 L45 60" fill="none" stroke={palette.accent} strokeWidth="1.5" />
              <circle cx="40" cy="30" r="3" fill="none" stroke={palette.secondary} />
            </g>
            <path d="M20 65 H60" stroke={palette.secondary} strokeWidth="1" opacity="0.4" />
          </g>
        )}

        {type === 'agent' && (
          <g>
            {/* Doorway / Portal - Primary Cyan */}
            <path d="M25 65 V30 Q40 20 55 30 V65" fill="none" stroke={palette.primary} strokeWidth="1" opacity="0.5" />
            {/* Floating Key - Accent Gold */}
            <g className="agent-key">
              <circle cx="35" cy="40" r="6" fill="none" stroke={palette.accent} strokeWidth="1.5" />
              <path d="M41 40 H55 V47 M50 40 V47" fill="none" stroke={palette.accent} strokeWidth="1.5" />
            </g>
            {/* Clearing Ripples */}
            <circle cx="45" cy="42" r="15" fill="none" stroke={palette.secondary} strokeWidth="0.5" strokeDasharray="4 2" className="animate-ping opacity-30" />
          </g>
        )}

        {type === 'business' && (
          <g>
            {/* Building Silhouette - Primary Slate */}
            <path d="M20 65 H35 V25 H50 V45 H60 V65H20" fill="none" stroke={palette.primary} strokeWidth="1.5" />
            {/* Upward Energy/Growth - Accent Emerald Green */}
            <path d="M27 55 L27 45" stroke={palette.accent} strokeWidth="2" strokeLinecap="round" className="biz-arrow" />
            <path d="M42 55 L42 35" stroke={palette.accent} strokeWidth="2" strokeLinecap="round" className="biz-arrow [animation-delay:0.3s]" />
            <path d="M55 55 L55 50" stroke={palette.accent} strokeWidth="2" strokeLinecap="round" className="biz-arrow [animation-delay:0.6s]" />
            {/* Radiating Prosperity */}
            <circle cx="50" cy="20" r="5" fill="none" stroke={palette.secondary} strokeWidth="0.5" className="animate-ping" />
            <path d="M50 12 V15 M58 20 H55 M50 28 V25 M42 20 H45" stroke={palette.secondary} strokeWidth="1" />
          </g>
        )}
      </svg>
    </div>
  );
};

const WhoUsesIt = () => {
  const containerRef = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".audience-card", {
        opacity: 0,
        y: 60,
        duration: 1.2,
        stagger: 0.2,
        ease: "expo.out",
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top 80%",
          toggleActions: "play none none none"
        }
      });
    }, containerRef);
    return () => ctx.revert();
  }, []);

  return (
    <SectionWrapper ref={containerRef} id="who-it-is-for" className="bg-sage-mist/5 py-24">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-serif text-forest-green mb-6">
            Who It’s <span className="italic font-normal">For</span>
          </h2>
          <p className="text-lg text-gray-700 font-sans leading-relaxed">
            From private sanctuaries to professional developments, land healing brings
            vitality, balance, and resolution to any space where people live and work.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {audiences.map((item, index) => (
            <div
              key={index}
              className="audience-card p-10 bg-white/40 backdrop-blur-sm rounded-[2.5rem] border border-forest-green/10 flex flex-col items-center text-center hover:bg-white hover:shadow-xl hover:shadow-forest-green/5 transition-[background-color,box-shadow,border-color] duration-500 group"
            >
              <div className="transform group-hover:scale-110 group-hover:-translate-y-2 transition-transform duration-500">
                <AudienceIllustration
                  type={item.icon}
                  color={item.color}
                  glow={item.glow}
                  palette={item.palette}
                />
              </div>
              <h3 className={`text-2xl font-serif mb-4 ${item.color}`}>
                {item.title}
              </h3>
              <p className="text-gray-600 font-sans leading-relaxed text-sm md:text-base">
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </SectionWrapper>
  );
};

export default WhoUsesIt;
